'use client'

import SearchCharacterCard from '@/app/_components/Search/SearchResult/SearchCharacterCard'
import Skeleton from '@/app/_components/Shared/Skeleton'
import { cn } from '@/app/utils'
import { useInfiniteQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { fetchCharacters } from './actions'

// function SelectedCGButton(props: {
//   onRemove: () => void
//   children: React.ReactNode
// }) {
//   const { children, onRemove } = props
//   return (
//     <Button
//       type={'button'}
//       className={cn('px-16 py-8')}
//       disableHoverAnimation
//       variant="outlined"
//       size="xs"
//       color="black">
//       <span className={'truncate'}>{children}</span>
//       <svg
//         onClick={(e) => {
//           e.stopPropagation()
//           e.preventDefault()
//           onRemove()
//         }}
//         className="shrink-0"
//         width="20"
//         height="21"
//         viewBox="0 0 20 21"
//         fill="none"
//         xmlns="http://www.w3.org/2000/svg">
//         <path
//           fillRule="evenodd"
//           clipRule="evenodd"
//           d="M9.99935 18.8334C14.6017 18.8334 18.3327 15.1025 18.3327 10.5001C18.3327 5.89771 14.6017 2.16675 9.99935 2.16675C5.39698 2.16675 1.66602 5.89771 1.66602 10.5001C1.66602 15.1025 5.39698 18.8334 9.99935 18.8334ZM6.91009 13.5893C6.58466 13.2639 6.58466 12.7363 6.91009 12.4108L8.82086 10.5001L6.91017 8.58936C6.58473 8.26392 6.58473 7.73629 6.91017 7.41085C7.23561 7.08541 7.76325 7.08541 8.08868 7.41085L9.99937 9.32155L11.9101 7.41083C12.2355 7.08539 12.7632 7.08539 13.0886 7.41083C13.414 7.73626 13.414 8.2639 13.0886 8.58934L11.1779 10.5001L13.0887 12.4109C13.4141 12.7363 13.4141 13.2639 13.0887 13.5894C12.7632 13.9148 12.2356 13.9148 11.9102 13.5894L9.99937 11.6786L8.0886 13.5893C7.76317 13.9148 7.23553 13.9148 6.91009 13.5893Z"
//           fill="#CB311F"
//         />
//       </svg>
//     </Button>
//   )
// }

type QueryData = Awaited<ReturnType<typeof fetchCharacters>>

// function KeywordHistory({
//   max,
//   className,
// }: {
//   max: number
//   className?: string
// }) {
//   const [params, setParams] = useSearchParams(
//     z.object({
//       keywordHistory: stringArray(),
//     }),
//   )

//   const histories = params.keywordHistory.map((keyword, index) => (
//     <SelectedCGButton
//       key={index}
//       onRemove={() => {
//         setParams({
//           keywordHistory: params.keywordHistory.filter((k) => k !== keyword),
//         })
//       }}>
//       {keyword}
//     </SelectedCGButton>
//   ))

//   return (
//     <div
//       className={cn(
//         'flex max-w-300 shrink-0 flex-row items-center  gap-11 lg:max-w-none 2xl:max-w-none',
//         className,
//       )}>
//       {histories.slice(0, max)}
//       {histories.length > max && (
//         <svg
//           width="12"
//           height="4"
//           viewBox="0 0 12 4"
//           fill="none"
//           xmlns="http://www.w3.org/2000/svg">
//           <path
//             d="M1.224 3.208C0.648 3.208 0.168 2.76 0.168 2.104C0.168 1.432 0.648 0.968 1.224 0.968C1.8 0.968 2.28 1.432 2.28 2.104C2.28 2.76 1.8 3.208 1.224 3.208ZM5.67713 3.208C5.10113 3.208 4.62113 2.76 4.62113 2.104C4.62113 1.432 5.10113 0.968 5.67713 0.968C6.25313 0.968 6.73313 1.432 6.73313 2.104C6.73313 2.76 6.25313 3.208 5.67713 3.208ZM10.1303 3.208C9.55425 3.208 9.07425 2.76 9.07425 2.104C9.07425 1.432 9.55425 0.968 10.1303 0.968C10.7063 0.968 11.1863 1.432 11.1863 2.104C11.1863 2.76 10.7063 3.208 10.1303 3.208Z"
//             fill="black"
//           />
//         </svg>
//       )}
//     </div>
//   )
// }

export default function LoadMore({
  limit,
  cursor,
  sorting,
  selectedGenres,
  selectedCategories,
  keywords,
  keyword,
  initialRenderedCharacters,
  isVerified,
}: {
  limit: number
  cursor: string | null
  sorting: 'hot' | 'latest' | 'view-count' | 'rising' | 'verified'
  selectedGenres: string[]
  selectedCategories: string[]
  keywords: string[]
  keyword: string
  initialRenderedCharacters: React.ReactNode[] | React.ReactNode
  isVerified: 'y' | 'n'
}) {
  const { ref, inView } = useInView()
  const [enabled, setEnabled] = useState(false)
  const { data, isFetchingNextPage, fetchNextPage, isLoading, hasNextPage } =
    useInfiniteQuery<QueryData | null>({
      enabled,
      initialPageParam: cursor,
      queryKey: [
        'characters',
        {
          limit,
          cursor,
          selectedCategories,
          selectedGenres,
          keywords,
          keyword,
          sorting,
          isVerified,
        },
      ],
      getNextPageParam: (lastPage) => lastPage?.meta.nextCursor ?? undefined,
      queryFn: async ({ pageParam = null }) => {
        if (typeof pageParam !== 'string') {
          return null
        }

        return fetchCharacters({
          limit,
          sorting,
          keywords,
          keyword,
          genreIds: selectedGenres,
          categoryIds: selectedCategories,
          withCount: false,
          cursor: pageParam,
          excludeIds: [],
          isVerified,
        })
      },
    })

  const characters = useMemo(() => {
    const lst =
      data?.pages?.flatMap((p: QueryData | null) => p?.data ?? null) ?? []

    return lst.filter((c) => !!c)
  }, [data?.pages])

  const hasNext = cursor && (enabled ? hasNextPage : true)

  useEffect(() => {
    if (!enabled) setEnabled(true)
    if (isFetchingNextPage) return

    if (inView && hasNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, inView, hasNextPage, enabled, isFetchingNextPage])

  return (
    <div
      className={cn('flex flex-col items-center gap-40 ', {
        'flex-1': characters.length <= 0,
      })}>
      <div
        className={cn(
          'grid  w-full  gap-16 max-lg:flex max-lg:flex-col max-lg:items-center lg:grid-cols-2 lg:gap-24 xl:grid-cols-2 2xl:grid-cols-3 2xl:gap-32 3xl:grid-cols-5',
          { flex: characters.length <= 0 },
        )}>
        {initialRenderedCharacters}
        {characters.map((c) => (
          <SearchCharacterCard character={c!} key={c!.id} />
        ))}
      </div>
      {hasNext && !isLoading && <div className="mt-80 h-20 w-full" ref={ref} />}
    </div>
  )
}

export function LoadMoreSkeleton() {
  return (
    <div className={'flex flex-col items-center gap-40'}>
      <div className="grid w-full gap-16 max-lg:flex max-lg:flex-col max-lg:items-center lg:grid-cols-2 lg:gap-24 xl:grid-cols-2 2xl:grid-cols-3 2xl:gap-32 3xl:grid-cols-5">
        <Skeleton className="h-496 w-full rounded-20 lg:w-304 3xl:w-270" />
        <Skeleton className="h-496 w-full rounded-20 lg:w-304 3xl:w-270" />
        <Skeleton className="h-496 w-full rounded-20 lg:w-304 3xl:w-270" />
        <Skeleton className="h-496 w-full rounded-20 lg:w-304 3xl:w-270" />
        <Skeleton className="h-496 w-full rounded-20 lg:w-304 3xl:w-270" />
      </div>
    </div>
  )
}
